import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormSpy } from 'react-final-form';
import { connect } from 'react-redux';
import cx from 'classnames';
import { withAsyncActions } from 'react-async-client';
import { Button, Row, Tooltip, Spin } from 'antd';
import Translate from 'react-translate-component';
import { contains, filter, path, omit, sum, find } from 'ramda';

import withAutoFocusForm from 'components/hocs/withAutoFocusForm';
import { EVENT_RESULT_TYPES } from 'constants/events';
import { updateEvent } from 'actions/asyncActions';
import EventResultMetaForm from './EventResultMetaForm';
import FreeForm from './forms/FreeForm';
import XMLForm from './forms/XMLForm';
import FileUploader from './forms/FileUploader';
import ScriptForm from './forms/ScriptForm';
import { xmlFields } from 'constants/xmlDocumentTypes';
import withFormWrapper from 'components/hocs/withFormWrapper';
import ListenerField from 'components/common/form/ListenerField';

const { FREE, FILE, FORM, SCRIPT } = EVENT_RESULT_TYPES;

const clearExcess = (obj) => obj.shortResultBlock === null ?
                             omit(['shortResultBlock', 'validateCount'], obj) :
                             omit(['validateCount'], obj);
const isShortBlockAllowed = (props) => path(['item', 'result', 'shortResultBlock'], props);
const getTranslation = (isTookPlace) => <Translate content={ isTookPlace ? 'events.fillResult' : 'events.fillReason' } />;

const stateToProps = (state, { item }) => {
    const position = path(['details', 'project', '0', 'text'], item);

    return {
        event: { ...item, position }
    };
};

const isMoreThanOneAllowed = event => sum([
    !!event.result[FREE],
    !!(event.result[FORM] || event.result[SCRIPT]),
    !!event.result[FILE]
]) > 1;

const getSingleType = event => find(type => event.result[type], [FREE, FORM, FILE]) || FORM;

@withAutoFocusForm
@withAsyncActions({
    formAction: updateEvent
        .withParams(({ item }) => ({ id: item.id }))
        .withOptions({ autoReset: true })
})
@connect(stateToProps)
@withFormWrapper({
    scrollToInvalid: '.ant-modal-body',
    customSubmit: true,
})
export default class EventResultForm extends Component {
    static propTypes = {
        event: PropTypes.object.isRequired,
        item: PropTypes.object.isRequired,
        close: PropTypes.func.isRequired,
        type: PropTypes.string.isRequired,
        formAction: PropTypes.object.isRequired,
        form: PropTypes.object,
        handleSubmit: PropTypes.func,
        invalid: PropTypes.bool,
    }

    state = {
        resultType: isMoreThanOneAllowed(this.props.event) ? null : getSingleType(this.props.event)
    }

    active = (type) => this.state.resultType === type;

    setResultType = (resultType) => {
        const newResultType = this.state.resultType === resultType ? null : resultType;
        this.setState({ resultType: newResultType });
    };

    isSaveReady = () => {
        const { event : { result: { eventWithScript }} } = this.props;
        const { resultType } = this.state;

        return resultType && !(resultType === FORM && eventWithScript);
    }

    getResultButton = (resultType) => (
        <Button onClick={() => this.setResultType(resultType)} className={cx({ active: this.active(resultType) })}>
            <Translate content={`events.${resultType}`} />
        </Button>
    );

    getShortResults = (evaluatedScore, shortResults = []) => {
        return evaluatedScore ? (
            filter(result => contains(evaluatedScore, result.evaluatedScores), shortResults)
        ) : shortResults;
    }

    sendData = (result, shortResultBlock = null, onlyShortBlock) => {
        if (this.props.invalid) {
            return this.props.handleSubmit();
        }

        const { resultType } = this.state;
        const { formAction, type } = this.props;
        const { id, type: eventType, comment, details, result: { eventWithScript } } = this.props.event;
        const { eventDate, eventTime, participants } = details;
        const shortResult = isShortBlockAllowed(this.props) && onlyShortBlock;

        formAction.dispatch(clearExcess({
            id,
            contact: {
                type: eventType,
                project: path(['project', 0, 'id'], details),
                person: path(['people', 0, 'id'], details),
                employee: path(['responsible', 0, 'id'], details),
                organization: path(['company', 0, 'id'], details),
                contact: path(['contact', 0, 'id'], details),
                description: comment,
            },
            additionalPeople: participants && participants.map(item => ({
                peopleId: item.id
            })),
            details: {
                dateTo: eventDate,
                time: eventTime,
                shortResult,
            },
            shortResultBlock,
            result: {
                ...result,
                type,
                resultType,
                eventWithScript,
            }
        }));
    }

    onSubmit = ({ result, shortResultBlock }) => {
        this.sendData(result, shortResultBlock, false);
    }

    onSubmitOnlyShort = ({ result, shortResultBlock }) => {
        this.sendData(result, shortResultBlock, true);
    }

    onSubmitWithScript = (result, formValues) => {
        this.sendData(result, formValues.shortResultBlock, false);
    }

    render () {
        const { resultType } = this.state;
        const { close, event, item: { shortResults, abortedResultReasons }, type } = this.props;
        const { item: { result: { shortResultBlock, shortResult } }, form: { change } } = this.props;
        const { formAction: { meta: { pending } } } = this.props;

        const resultTypeChoosen = !!this.state.resultType;
        const shortBlockForbidden = !shortResultBlock;
        const formExist = xmlFields[type];

        const moreThanOneAllowed = isMoreThanOneAllowed(event);

        return (
            <Spin spinning={pending}>
                <ListenerField
                    listenFieldName={['shortResultBlock.tookPlace']}
                >
                    { ({ shortResultBlock: { tookPlace } }) => {
                        const isTookPlace = tookPlace === 1;

                        return (
                            <div className="card-form panel-view" noValidate>
                                { shortResultBlock && (
                                    <ListenerField
                                        listenFieldName={['shortResultBlock.evaluatedScore', 'shortResultBlock.resultCode']}
                                    >
                                        { ({ shortResultBlock : { evaluatedScore, resultCode } }) => (
                                            <EventResultMetaForm
                                                shortResults={this.getShortResults(evaluatedScore, shortResults)}
                                                abortedResultReasons={abortedResultReasons}
                                                tookPlace={tookPlace}
                                                isTookPlace={isTookPlace}
                                                evaluatedScore={evaluatedScore}
                                                resultCode={resultCode}
                                                change={change}
                                            />
                                        )}
                                    </ListenerField>
                                )}
                                { (tookPlace || shortBlockForbidden) &&
                                    <div>
                                        <Row
                                            className={cx('card-bottom event-buttons', { 'empty-event-buttons': !moreThanOneAllowed })}
                                        >
                                            { shortResult && shortResultBlock && (
                                                <ListenerField
                                                    listenFieldName={['shortResultBlock.resultCode']}
                                                >
                                                    { ({ shortResultBlock: { resultCode } }) => {
                                                        const resultNotSelected = !resultCode;

                                                        return (
                                                            <Tooltip title={resultNotSelected && getTranslation(isTookPlace)} placement="top">
                                                                <FormSpy>
                                                                    { ({ values }) => (
                                                                        <Button
                                                                            onClick={() => this.onSubmitOnlyShort(values)}
                                                                            disabled={(resultTypeChoosen && moreThanOneAllowed) || resultNotSelected}
                                                                        >
                                                                            <Translate content="events.saveShort" />
                                                                        </Button>
                                                                    )}
                                                                </FormSpy>
                                                            </Tooltip>
                                                        )
                                                    }}
                                                </ListenerField>
                                            )}
                                            { moreThanOneAllowed &&
                                                <Button.Group className={shortResultBlock ? 'pull-right' : 'pull-left'}>
                                                    { event.result[FREE] && this.getResultButton(FREE) }
                                                    { (event.result[FORM] || event.result[SCRIPT]) && this.getResultButton(FORM) }
                                                    { event.result[FILE] && this.getResultButton(FILE) }
                                                </Button.Group>
                                            }
                                        </Row>

                                        { resultType === FORM && (
                                            event.result.eventWithScript ? (
                                                <FormSpy>
                                                    { ({ values }) => (
                                                        <ScriptForm
                                                            save={(result) => this.onSubmitWithScript(result, values)}
                                                            {...this.props}
                                                        />
                                                    )}
                                                </FormSpy>
                                            ) : (
                                                <XMLForm {...this.props} />
                                            )
                                        )}
                                        { resultType === FILE && <FileUploader required {...this.props} /> }
                                        { resultType === FREE && <FreeForm {...this.props} /> }
                                        <Row className="card-bottom m-t-30">
                                            { this.isSaveReady() && (formExist || resultType !== FORM) &&
                                                <FormSpy>
                                                    { ({ values }) => (
                                                        <Button className="card-button" type="primary" onClick={() => this.onSubmit(values)}>
                                                            <Translate content="events.saveEventResult" />
                                                        </Button>
                                                    )}
                                                </FormSpy>
                                            }
                                        </Row>
                                    </div>
                                }
                                <Button
                                    className={cx('card-button close-button event-result-cancel', { 'single-event-result-cancel': !moreThanOneAllowed })}
                                    onClick={close}
                                >
                                    <Translate content="events.cancel" />
                                </Button>
                            </div>
                        );
                    }}
                </ListenerField>
            </Spin>
        );
    }
}
